import React, { useState } from "react";
import { usePortfolio } from "@/context/PortfolioContext";
import { Briefcase, Plus, Check, Pencil, Trash2, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function PortfolioSwitcher() {
  const { portfolios, activeId, activePortfolio, select, create, rename, remove } = usePortfolio();
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const name = createName.trim();
    if (!name) return toast.error("Enter a portfolio name");
    setBusy(true);
    try {
      await create(name);
      setCreateName("");
      setCreateOpen(false);
      toast.success(`Portfolio "${name}" created`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="portfolio-switcher"
            className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded border transition-colors hover:bg-white/5 max-w-[220px]"
            style={{ borderColor: "var(--btn-secondary-border)", color: "var(--text-primary)" }}
          >
            <Briefcase className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} style={{ color: "var(--accent-primary)" }} />
            <span className="truncate font-medium">
              {activePortfolio?.name || "Portfolio"}
            </span>
            <span className="text-[10px] label-overline ml-1">
              {portfolios.length > 1 ? `${portfolios.length}` : ""}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 rounded-xl" data-testid="portfolio-menu">
          <DropdownMenuLabel className="label-overline text-[10px]">Switch portfolio</DropdownMenuLabel>
          {portfolios.map((p) => (
            <DropdownMenuItem
              key={p.id}
              data-testid={`pf-select-${p.id}`}
              onClick={() => select(p.id)}
              className="flex items-center justify-between cursor-pointer"
            >
              <span className="flex items-center gap-2 truncate">
                <Briefcase className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} style={{ color: p.is_default ? "var(--accent-primary)" : "var(--text-muted)" }} />
                <span className="truncate">{p.name}</span>
                {p.is_default && <span className="text-[9px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>· default</span>}
              </span>
              {p.id === activeId && <Check className="w-3.5 h-3.5" strokeWidth={2} style={{ color: "var(--accent-primary)" }} />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="pf-create-open" onClick={() => setCreateOpen(true)} className="cursor-pointer">
            <Plus className="w-3.5 h-3.5 mr-2" strokeWidth={1.75} /> New portfolio
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="pf-manage-open" onClick={() => setManageOpen(true)} className="cursor-pointer">
            <Pencil className="w-3.5 h-3.5 mr-2" strokeWidth={1.75} /> Manage portfolios
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="pf-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[21px] font-semibold tracking-[-0.015em]">New portfolio</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <Input
              data-testid="pf-create-name"
              autoFocus
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="e.g. Long-term, Trading, Family"
              className="rounded-lg"
              maxLength={60}
            />
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-xl">Cancel</Button>
              <Button type="submit" data-testid="pf-create-submit" disabled={busy} className="rounded" style={{ background: "var(--btn-primary-bg)" }}>
                {busy ? "Creating…" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage dialog */}
      <ManageDialog open={manageOpen} onClose={() => setManageOpen(false)} portfolios={portfolios} rename={rename} remove={remove} />
    </>
  );
}

function ManageDialog({ open, onClose, portfolios, rename, remove }) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const startEdit = (p) => { setEditingId(p.id); setEditName(p.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(""); };

  const saveEdit = async (p) => {
    const name = editName.trim();
    if (!name) return toast.error("Name is required");
    setBusy(true);
    try {
      await rename(p.id, name);
      toast.success("Renamed");
      cancelEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally { setBusy(false); }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete portfolio "${p.name}" and all its transactions? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await remove(p.id);
      toast.success("Portfolio deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="pf-manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[21px] font-semibold tracking-[-0.015em]">Manage portfolios</DialogTitle>
        </DialogHeader>
        <ul className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto">
          {portfolios.map((p) => (
            <li key={p.id} className="surface-card p-3 flex items-center gap-2" data-testid={`pf-manage-row-${p.id}`}>
              {editingId === p.id ? (
                <>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-lg flex-1" autoFocus />
                  <Button size="sm" onClick={() => saveEdit(p)} disabled={busy} className="rounded" style={{ background: "var(--btn-primary-bg)" }}>Save</Button>
                  <Button size="sm" variant="outline" onClick={cancelEdit} className="rounded-lg">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </>
              ) : (
                <>
                  <Briefcase className="w-4 h-4 shrink-0" strokeWidth={1.75} style={{ color: p.is_default ? "var(--accent-primary)" : "var(--text-muted)" }} />
                  <span className="flex-1 truncate">{p.name}{p.is_default && <span className="text-[10px] ml-2 uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>default</span>}</span>
                  <button data-testid={`pf-rename-${p.id}`} onClick={() => startEdit(p)} className="p-1.5 rounded hover:bg-white/5">
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                  <button
                    data-testid={`pf-delete-${p.id}`}
                    onClick={() => onDelete(p)}
                    disabled={portfolios.length <= 1}
                    className="p-1.5 rounded hover:bg-white/5 disabled:opacity-30"
                    style={{ color: "var(--accent-negative)" }}
                    title={portfolios.length <= 1 ? "Cannot delete the only portfolio" : "Delete portfolio and its transactions"}
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
